import { useLanguage } from '@/hooks/useLanguage';
import { ProcessStep } from '@/components/ProcessStep';
import { Package, Play, CheckCircle, FileText, Search } from 'lucide-react';

export const HowItWorks = () => {
  const { t } = useLanguage();

  const steps = [
    {
      title: t.howItWorks.steps.prepare.title,
      description: t.howItWorks.steps.prepare.description,
      icon: Package,
    },
    {
      title: t.howItWorks.steps.runCycle.title,
      description: t.howItWorks.steps.runCycle.description,
      icon: Play,
    },
    {
      title: t.howItWorks.steps.checkIndicator.title,
      description: t.howItWorks.steps.checkIndicator.description,
      icon: CheckCircle,
    },
    {
      title: t.howItWorks.steps.recordTrace.title,
      description: t.howItWorks.steps.recordTrace.description,
      icon: FileText,
    },
    {
      title: t.howItWorks.steps.qualityReview.title,
      description: t.howItWorks.steps.qualityReview.description,
      icon: Search,
    },
  ];

  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {t.howItWorks.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t.howItWorks.description}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto mt-6 rounded-full" />
        </div>

        {/* Desktop Timeline */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-5 gap-8 relative">
            {steps.map((step, index) => (
              <ProcessStep
                key={index}
                step={index + 1}
                title={step.title}
                description={step.description}
                icon={step.icon}
                isLast={index === steps.length - 1}
              />
            ))}
          </div>
        </div>

        {/* Mobile Timeline */}
        <div className="lg:hidden space-y-12">
          {steps.map((step, index) => (
            <div key={index} className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center shadow-lg relative">
                  <step.icon className="w-8 h-8 text-white" />
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-secondary rounded-full flex items-center justify-center text-white font-bold text-xs">
                    {index + 1}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className="w-px h-12 bg-gradient-to-b from-primary/50 to-transparent mx-auto mt-4" />
                )}
              </div>
              <div className="flex-1 pt-2">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Info */}
        <div className="mt-16 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            {t.training.title}
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            {t.training.description}
          </p>
          <button
            onClick={() => {
              const element = document.querySelector('#contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:shadow-lg"
          >
            {t.training.button}
          </button>
        </div>
      </div>
    </section>
  );
};